import type { FilterDictionary } from '@moment/horizon-data-access/proposal'
import type { FilterObject } from '@moment/trading-types/filter-expression'

import { Operator } from '@moment/trading-types/filter'
import { isAndCondition } from '@moment/trading-types/filter-expression'
import { BondType, IceSector1 } from '@moment/trading-types/reference-data'

// An empty `and: []` / `or: []` restricts nothing but survives `hasScope`.
function withoutEmptyCompounds(clause: FilterObject): FilterObject | undefined {
  const kept = Object.entries(clause as Record<string, unknown>).filter(
    ([key, condition]) =>
      !(
        (key === 'and' || key === 'or') &&
        Array.isArray(condition) &&
        condition.length === 0
      ),
  )
  return kept.length > 0
    ? (Object.fromEntries(kept) as FilterObject)
    : undefined
}

export function flattenAndClauses(
  scope: FilterObject | undefined,
): FilterObject[] {
  if (scope === undefined) return []
  if (isAndCondition(scope) && scope.and.length > 0) {
    const { and, ...siblings } = scope
    const nested = and.flatMap(flattenAndClauses)
    const leading = withoutEmptyCompounds(siblings as FilterObject)
    return leading === undefined ? nested : [leading, ...nested]
  }
  const leaf = withoutEmptyCompounds(scope)
  return leaf === undefined ? [] : [leaf]
}

// none → undefined, one → the clause, many → an `and` wrapper.
export function composeScope(
  clauses: FilterObject[],
): FilterObject | undefined {
  if (clauses.length === 0) return undefined
  if (clauses.length === 1) return clauses[0]
  return { and: clauses } as FilterObject
}

const NO_OP_SCOPE_DIMENSIONS: Record<string, string[]> = {
  type: Object.values(BondType),
  ice_sector_1: Object.values(IceSector1),
}

// `{ dimension: { in: [...every value] } }` and nothing else.
function isNoOpClause(clause: FilterObject): boolean {
  const [entry, ...rest] = Object.entries(clause as Record<string, unknown>)
  if (entry === undefined || rest.length > 0) return false

  const [dimension, condition] = entry
  const allValues = NO_OP_SCOPE_DIMENSIONS[dimension]
  if (allValues === undefined) return false
  if (condition === null || typeof condition !== 'object') return false

  // A condition's operators AND, so `in` has to stand alone: a sibling narrows
  // the clause, and a `not_in` covering every value is an empty universe.
  const [operator, ...siblings] = Object.entries(
    condition as Record<string, unknown>,
  )
  if (operator === undefined || siblings.length > 0) return false

  const [name, values] = operator
  if (name !== Operator.In || !Array.isArray(values)) return false
  return allValues.every((value) => values.includes(value))
}

/**
 * A clause listing every value of its dimension restricts nothing, so it is
 * dropped rather than read back as a real scope or re-persisted on save.
 */
export function stripNoOpScope(
  scope: FilterObject | FilterDictionary | null | undefined,
): FilterObject | undefined {
  if (scope == null) return undefined
  // FilterDictionary is the API's untyped view of the same filter JSON.
  return composeScope(
    flattenAndClauses(scope as FilterObject).filter(
      (clause) => !isNoOpClause(clause),
    ),
  )
}
